import React, { FC, ReactElement, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { environment } from '../../../environments/environment';

interface videoProps {
  src: string;
  type: string;
  isShadow: boolean;
  shape: string;
  round: string;
  width: string;
  height: number;
  verticalAlignment: string;
  horizontalAlignment: string;
  code: any;
  controls: boolean;
  loop: boolean;
  autoplay: boolean;
  anchorName: string;
  anchorTrigger: string;
  editorPlugins: Record<string, any>;
}

interface videoWrapperProps {
  verticalAlignment: string;
  horizontalAlignment: string;
}

interface videoLayoutProps {
  width: string;
  round: string;
  shape: string;
  isShadow: boolean;
  height: number;
}

interface videoElementProps {
  round: string;
}

const VideoWrapper = styled.div<videoWrapperProps>`
  &.videoWrapper {
    display: flex;
    position: relative;
    align-items: ${(props) => props.verticalAlignment || 'inherit'};
    justify-content: ${(props) => props.horizontalAlignment || 'inherit'};
    @media (max-width: 768px) {
      align-items: center;
      justify-content: center;
    }
  }
`;

const VideoLayout = styled.div<videoLayoutProps>`
  &.videoLayout {
    border-radius: ${(props) => (props.round ? props.round : '0')};
    height: auto;
    width: ${(props) => props.width + '%' || '100%'};
    max-width: 100%;
    box-shadow: ${(props) => (props.isShadow ? 'rgba(0, 0, 0, 0.15) 0px 4px 8px' : 'none')};
    @media (max-width: 768px) {
      width: 100%;
    }
    .video__container {
      position: relative;
      overflow: hidden;
      width: 100%;
      video,
      img,
      iframe {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
    &.noVideo {
      .video__container {
        img {
          height: 100%;
          width: 100%;
          object-fit: cover;
        }
      }
    }
    ${(props) =>
      props.shape === 'circle' &&
      `
        border-radius: 100% !important;
        .video__container {
            border-radius: 100% !important;
            padding-top: 100%;
        }`};
    ${(props) =>
      props.shape === 'square' &&
      `
        .video__container {
            border-radius: 0 !important;
            padding-top: 100%;
        }`};
    ${(props) =>
      props.shape === '16-9' &&
      `
        .video__container {
            padding-top: 56.25%;
        }
    `};
    ${(props) => {
      if (props.shape === 'free' || !props.shape)
        return `
            .video__container {
                video, img, iframe {
                    position: static;
                    display: block;
                    object-fit: contain;
                }
            }
        `;
    }};
    ${(props) =>
      props.shape === 'axis-resize' &&
      `
        .video__container {
            height: ${props.height ? props.height + 'px' : 'auto'};
            padding-top: ${props.height ? 'unset' : '56.25%'};
        }
    `};
    @media (max-width: 768px) {
      width: 100%;
      ${(props) => props.width && Number(props.width) < 50 && ` width:50%`};
    }
    .iframeWrapper {
      &::before {
        filter: ${(props) =>
          props.round
            ? props.shape === 'square'
              ? 'blur(0)'
              : 'blur(' + props.round + ')'
            : 'none'};
      }
      iframe {
        border-radius: ${(props) => (props.round ? props.round : '0')};
        clippath: ${(props) =>
          props.round ? 'inset(0% 0% 0% 0% round ' + props.round + ')' : 'none'};
      }
    }
  }
`;

const VideoElement = styled.video<videoElementProps>`
  &.libraryVideo {
    border-radius: ${(props) => (props.round ? props.round : '0')};
    ${(props) =>
      (props.round === '100%' || props.round === '100px') &&
      `
      border-radius: 100%;
      height:100%;
  `};
  }
`;

const Video: FC<videoProps> = ({ ...props }): ReactElement => {
  const elementRef = useRef<any>(null);
  const [width, setWidth] = useState<string>(props.width);
  const [triggerInitialHeight, setTriggerInitialHeight] = useState<boolean>(false);
  const [height, setHeight] = useState<number>(props.height);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const [elementDomWidth, setElementDomWidth] = useState(elementRef.current?.clientWidth || 100);
  const [videoContainerWidth, setVideoContainerWidth] = useState(
    videoContainerRef?.current?.clientWidth,
  );
  const [videoContainerHeight, setVideoContainerHeight] = useState<number>(0);
  const ResizeRatio = props.editorPlugins?.ResizeRatio;
  const ResizeAxis = props.editorPlugins?.ResizeAxis;

  function isShowPlaceHolder() {
    return (
      (props.type === 'Library' && !props.src) ||
      (props.type === 'Youtube' && !props.src) ||
      (props.type === 'Code' && !props.code)
    );
  }

  useEffect(() => {
    if (elementRef && elementRef.current) setElementDomWidth(elementRef.current.clientWidth);
    if (videoContainerRef && videoContainerRef.current) {
      setVideoContainerWidth(videoContainerRef.current.clientWidth);
      setVideoContainerHeight(videoContainerRef.current.clientHeight);
    }
  }, [width, height, props.shape]);

  useEffect(() => {
    setWidth(props.width);
  }, [props.width, props.height]);

  useEffect(() => {
    if (isNaN(props.height) && props.shape === 'axis-resize') {
      if (!height && videoContainerRef && videoContainerRef.current)
        setHeight(videoContainerRef.current.clientHeight);
      setTriggerInitialHeight(true);
    }
  }, [props.shape]);

  const emitWidth = (width: string): void => setWidth(width);
  const emitHeight = (height: number): void => setHeight(height);

  return (
    <VideoWrapper
      className={'videoWrapper'}
      verticalAlignment={props.verticalAlignment}
      horizontalAlignment={props.horizontalAlignment}
      ref={elementRef}>
      {props.shape === 'axis-resize'
        ? ResizeAxis && (
            <ResizeAxis
              widthProps={{
                width,
                property: 'width',
                min: 10,
                max: 100,
                elementDomWidth,
                containerWidth: videoContainerWidth,
                emitWidth,
              }}
              heightProps={{
                height,
                property: 'height',
                min: 50,
                max: 1500,
                containerHeight: videoContainerHeight,
                emitHeight,
              }}
              triggerInitialHeight={triggerInitialHeight}
            />
          )
        : ResizeRatio && (
            <ResizeRatio
              width={width}
              property="width"
              min={10}
              max={100}
              suffix={'%'}
              elementDomWidth={elementDomWidth}
              elementContainerWidth={videoContainerWidth}
              emitWidth={emitWidth}
            />
          )}
      <VideoLayout
        className={'videoLayout ' + (isShowPlaceHolder() ? 'noVideo' : '')}
        round={props.round}
        width={width}
        height={height}
        isShadow={props.isShadow}
        shape={props.shape}
        ref={videoContainerRef}>
        <div className="video__container">
          {props.type === 'Library' && props.src.length > 24 && (
            <VideoElement
              className={'libraryVideo'}
              key={
                props.src ||
                props.autoplay.toString() ||
                props.controls.toString() ||
                props.loop.toString() ||
                props.shape.toString()
              }
              autoPlay={props.autoplay}
              loop={props.loop}
              muted
              round={props.round}
              controls={props.controls}>
              <source src={props.src} type="video/mp4" />
            </VideoElement>
          )}
          {props.type === 'Youtube' && props.src.length > 24 && (
            <div className={'iframeWrapper'} key={props.src}>
              <iframe
                src={props.src + (props.controls ? '' : '?controls=0')}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen></iframe>
            </div>
          )}
          {props.type === 'Code' && props.code && props.code.length && (
            <div className={'iframeWrapper'} key={props.code}>
              <div className="embeddedCode" dangerouslySetInnerHTML={{ __html: props.code }}></div>
            </div>
          )}
          {isShowPlaceHolder() && (
            <div className="noVideo placeholder">
              <img src={environmentLink + '/assets/images/videpPlcaeHolder.svg'} alt="No video" />
            </div>
          )}
        </div>
      </VideoLayout>
    </VideoWrapper>
  );
};

const environmentLink: string = environment().assetsLink;
export const settings: any = [
  [
    //group
    {
      settingName: 'HorizontalAlignment',
    },
  ],
  [
    //group
    {
      settingName: 'AddVideo',
      isMainSettings: true,
      title: 'Video',
    },
  ],
  [
    //group
    {
      settingName: 'Size',
      controls: [
        {
          controlName: 'Slider',
          title: 'Size',
          property: 'width',
          defaultValue: 70,
          suffix: '%',
          min: 10,
        },
      ],
    },
    {
      settingName: 'Shape',
      dropWidth: 'big-xxl',
      controls: [
        {
          controlName: 'ImageSelect',
          property: 'shape',
          title: 'Shape',
          defaultValue: 'square',
          useValueAndTitle: true,
          grayscale: true,
          presetData: [
            {
              title: 'Default',
              icon: environmentLink + '/assets/images/Image-select/default.svg',
              value: 'free',
              width: 20,
            },
            {
              title: 'Square',
              icon: environmentLink + '/assets/images/Image-select/square.svg',
              value: 'square',
              width: 20,
            },
            {
              title: 'Circle',
              icon: environmentLink + '/assets/images/Image-select/circle.svg',
              value: 'circle',
              width: 20,
            },
            {
              title: 'Rectangle',
              icon: environmentLink + '/assets/images/Image-select/rectangle.svg',
              value: '16-9',
              width: 20,
            },
            {
              title: 'Free form',
              icon: environmentLink + '/assets/images/Image-select/free.svg',
              value: 'axis-resize',
              width: 20,
            },
          ],
        },
      ],
    },
    {
      settingName: 'BorderRadius',
    },
    {
      settingName: 'Shadow',
    },
  ],
  [
    {
      settingName: 'VideoSettings',
    },
  ],
  [
    {
      settingName: 'Anchor',
    },
  ],
];

//element options
const options = [
  {
    name: 'src',
    title: 'Change',
    icon: environmentLink + '/assets/images/change-video.svg',
    action: 'updateVideo',
    settingTitle: 'Video',
  },
];

export default {
  Video,
  settings,
  options,
  label: 'Video',
};
